import { useId, useMemo, useState } from "react";
import { TrashIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import { Select } from "@/components/Common/Select";
import {
  Select as BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Tooltip } from "@/components/Common/Tooltip";
import { ActionFieldLabel } from "@/components/Automations/ActionFieldLabel";
import { Option } from "@/types/v2/UI";
import { TaskType } from "@/types/v2/Task";
import { DUE_DATE_UNITS, DueDateUnit } from "@/lib/taskDueDate";
import { TASK_TYPE_COLORS, TASK_TYPE_LABELS } from "@/lib/const";
import {
  CreateTaskActionValue as CreateTaskValue,
  encodeCreateTaskActionValue,
  parseCreateTaskActionValue,
} from "@/lib/automationCreateTask";

// Derived from the enum and TASK_TYPE_LABELS rather than hard-coded, so this
// block cannot drift from TaskForm.tsx again — it previously called
// TaskType.UPDATE "Customer Update" while the task form called it "Update" —
// and so a newly added TaskType shows up here without a second edit.
// Typed to TaskType, not the default `string`, so TASK_TYPE_COLORS can be
// indexed by an option's value without a cast.
const TYPE_OPTIONS: Option<TaskType>[] = Object.values(TaskType).map(
  (type) => ({
    label: TASK_TYPE_LABELS[type],
    value: type,
  })
);
const UNIT_OPTIONS: Option[] = DUE_DATE_UNITS.map((u) => ({
  label: u,
  value: u,
}));

export interface CreateTaskActionValueProps {
  label: string;
  value: string | number | null;
  assigneeOptions: Option[];
  onChange?: (raw: string) => void;
  onRemove?: () => void;
  canRemove?: boolean;
  readOnly?: boolean;
}

export const CreateTaskActionValue = ({
  label,
  value,
  assigneeOptions,
  onChange,
  onRemove,
  canRemove = true,
  readOnly,
}: CreateTaskActionValueProps) => {
  const parsed = useMemo(() => parseCreateTaskActionValue(value), [value]);
  const isReadOnly = readOnly || !onChange;

  // useId must run before the isReadOnly early return — hooks cannot be
  // called conditionally.
  const fieldId = useId();
  const titleId = `${fieldId}-title`;
  const descriptionId = `${fieldId}-description`;
  const assigneeSelectId = `${fieldId}-assignee`;
  const typeId = `${fieldId}-type`;
  const dueAmountId = `${fieldId}-due-amount`;

  // The chosen unit has to be held here, not in the action value: `dueDate`
  // requires an `amount`, so the encoded value has nowhere to record
  // "minutes, amount not entered yet". Without this the unit control
  // discarded every selection made before an amount was typed, and — worse —
  // the amount typed afterwards silently fell back to days.
  // `parsed.dueDate` stays the source of truth once an amount exists; this is
  // only the fallback for the period before that, and after the amount is
  // cleared again.
  const [pendingUnit, setPendingUnit] = useState<DueDateUnit>("days");
  const unit = parsed.dueDate?.unit ?? pendingUnit;

  const emit = (next: CreateTaskValue) =>
    onChange?.(encodeCreateTaskActionValue(next));

  if (isReadOnly) {
    const parts = [
      `"${parsed.title || "(untitled)"}"`,
      `type: ${parsed.type}`,
      parsed.assigneeId
        ? `assignee: ${assigneeOptions.find((o) => o.value === parsed.assigneeId)?.label ?? parsed.assigneeId}`
        : null,
      parsed.dueDate
        ? `due ${parsed.dueDate.amount} ${parsed.dueDate.unit} after trigger`
        : "no due date",
    ].filter(Boolean);
    return (
      <div
        className={"flex flex-row items-center gap-2 text-base text-primary"}
      >
        <span>{label}:</span>
        <span>{parts.join(" · ")}</span>
      </div>
    );
  }

  return (
    <div className={"flex flex-col gap-3 rounded-lg border border-border p-3"}>
      <div className={"flex flex-row items-center justify-between"}>
        <span className={"text-base text-primary"}>{label}</span>
        {canRemove && (
          <Tooltip message={"Remove"}>
            <Button size="icon" variant="destructive-alt" onClick={onRemove}>
              <TrashIcon />
            </Button>
          </Tooltip>
        )}
      </div>

      <div className={"flex flex-col gap-1"}>
        <ActionFieldLabel htmlFor={titleId} required>
          {"Task title"}
        </ActionFieldLabel>
        <Input
          id={titleId}
          variant={"minimal"}
          value={parsed.title}
          onChange={(e) => emit({ ...parsed, title: e.target.value })}
        />
      </div>

      <div className={"flex flex-col gap-1"}>
        <ActionFieldLabel htmlFor={descriptionId}>
          {"Description"}
        </ActionFieldLabel>
        <Input
          id={descriptionId}
          variant={"minimal"}
          value={parsed.description ?? ""}
          onChange={(e) =>
            emit({ ...parsed, description: e.target.value || undefined })
          }
        />
      </div>

      <div className={"flex flex-col gap-1"}>
        <ActionFieldLabel htmlFor={assigneeSelectId}>
          {"Assignee"}
        </ActionFieldLabel>
        <Select
          selectTriggerProps={{ variant: "minimal", id: assigneeSelectId }}
          placeholder={"Unassigned"}
          value={parsed.assigneeId ?? ""}
          options={assigneeOptions}
          onValueChange={(v) => emit({ ...parsed, assigneeId: v || undefined })}
        />
      </div>

      <div className={"flex flex-col gap-1"}>
        <ActionFieldLabel htmlFor={typeId}>{"Type"}</ActionFieldLabel>
        {/*
         * Radix primitives rather than Common/Select: `Option.label` is typed
         * `string`, so the options API cannot carry the colour swatch at all.
         * The markup below is TaskForm.tsx's Type field, so the two places a
         * task's type is chosen read identically.
         */}
        <BaseSelect
          value={parsed.type}
          onValueChange={(v) => emit({ ...parsed, type: v as TaskType })}
        >
          <SelectTrigger id={typeId} variant={"minimal"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <span
                  className={"mr-2 inline-block h-2.5 w-2.5 rounded-full"}
                  style={{ backgroundColor: TASK_TYPE_COLORS[option.value] }}
                />
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </BaseSelect>
      </div>

      <div className={"flex flex-col gap-1"}>
        <ActionFieldLabel htmlFor={dueAmountId}>{"Due"}</ActionFieldLabel>
        <div className={"flex flex-row items-center gap-2"}>
          <Input
            id={dueAmountId}
            type={"number"}
            min={0}
            step={1}
            variant={"minimal"}
            className={"w-20"}
            placeholder={"none"}
            value={parsed.dueDate ? String(parsed.dueDate.amount) : ""}
            onChange={(e) => {
              const amount = Math.floor(Number(e.target.value));
              if (!e.target.value || Number.isNaN(amount) || amount < 0) {
                // Carry the unit over as the pending one, so clearing the
                // amount does not also silently reset the unit on screen.
                setPendingUnit(unit);
                const { dueDate: _omit, ...rest } = parsed;
                emit(rest);
              } else {
                emit({ ...parsed, dueDate: { amount, unit } });
              }
            }}
          />
          <Select
            selectTriggerProps={{
              variant: "minimal",
              "aria-label": "Due date unit",
            }}
            value={unit}
            options={UNIT_OPTIONS}
            onValueChange={(u) => {
              const next = u as DueDateUnit;
              setPendingUnit(next);
              // Only emit when there is an amount to attach the unit to —
              // a unit alone is not a due date. The state above is what
              // makes the choice survive until an amount arrives.
              if (parsed.dueDate) {
                emit({ ...parsed, dueDate: { ...parsed.dueDate, unit: next } });
              }
            }}
          />
          <span className={"text-sm text-primary"}>{"after trigger"}</span>
        </div>
      </div>
    </div>
  );
};
